import { EntityId } from "@reduxjs/toolkit";
import { Pagination, Box, IconButton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { liveStreamingThunks } from "./store/slice";
import { selectDevicesBySiteId } from "features/devices/store/selectors";
import { useMemo } from "react";
import { selectLiveStreamingConfiguration } from "features/config/store/selectors";
import { liveStreamingSelectors } from "./store/selectors";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

export interface LiveStreamingDevicePaginationProps {
    siteId: EntityId;
}

export function LiveStreamingDevicePagination(
    props: LiveStreamingDevicePaginationProps,
) {
    const dispatch = useAppDispatch();

    const liveStreamingConfiguration = useAppSelector(
        selectLiveStreamingConfiguration,
    );

    const allDevicesInSite = useAppSelector(
        (state) => props.siteId && selectDevicesBySiteId(state, props.siteId),
    );

    const currentPage = useAppSelector((state) =>
        liveStreamingSelectors.selectDeviceSelectionForSitePageNumber(
            state,
            props.siteId,
        ),
    );

    // Check if another component interacted with the device selection (Reset paging component in that case)
    const pagingEnabled = useAppSelector(
        (state) =>
            props.siteId &&
            liveStreamingSelectors.selectDeviceSelectionForSiteIsDoneByPaging(
                state,
                props.siteId,
            ),
    );

    const pageCount = useMemo(() => {
        if (
            !allDevicesInSite ||
            allDevicesInSite.length === 0 ||
            !liveStreamingConfiguration?.paginationPageSize
        )
            return undefined;
        return Math.ceil(
            allDevicesInSite.length /
                liveStreamingConfiguration?.paginationPageSize,
        );
    }, [allDevicesInSite, liveStreamingConfiguration?.paginationPageSize]);

    return (
        liveStreamingConfiguration?.paginationPageSize && (
            <>
                {
                    <Box
                        sx={() => ({
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginBottom: 2,
                        })}
                    >
                        {!pagingEnabled && (
                            <IconButton
                                title="Reset pagination"
                                onClick={() => {
                                    // When using the reset button:
                                    // We will reset the paging to the first page and enable the devices on the first page
                                    const resetToPageNumber = 1;

                                    dispatch(
                                        liveStreamingThunks.setSelectedDevicesForSiteByPagingAsync(
                                            {
                                                siteId: props.siteId,
                                                pageSize:
                                                    liveStreamingConfiguration?.paginationPageSize,
                                                pageNumber: resetToPageNumber,
                                            },
                                        ),
                                    );
                                }}
                            >
                                <RestartAltIcon />
                            </IconButton>
                        )}

                        <Pagination
                            count={pageCount}
                            variant="outlined"
                            page={currentPage}
                            onChange={(_, pageNumber: number) => {
                                // Set paginated set of devices as active
                                dispatch(
                                    liveStreamingThunks.setSelectedDevicesForSiteByPagingAsync(
                                        {
                                            siteId: props.siteId,
                                            pageSize:
                                                liveStreamingConfiguration?.paginationPageSize,
                                            pageNumber: pageNumber,
                                        },
                                    ),
                                );
                            }}
                        />
                    </Box>
                }
            </>
        )
    );
}
